"use client";

import { c as _c } from "react/compiler-runtime";
import { formatCurrency } from "@/lib/format-data";
import { type DurationBound, atDurationBound, formatDurationRange } from "@/lib/provider-service-duration";
import { quotesWithAddons } from "@/lib/service-addon-utils";
import { formatDisplayPrice } from "@/lib/service-fees";
import { computeAddonMinutes, computePatientFacingDurationWithAddons, patientFacingDurationLabel } from "@/lib/service-utils";
import { cx } from "@/lib/tailwind-utils";
import { PublicServiceAddon, ServiceTiny } from "@/types/service";
import { getServicePricingForSelfSchedule } from "./helpers";
interface SelectionsSummaryProps {
  basePrice: number;
  basePriceMax?: number;
  /** What the base service bills; see `quotesWithAddons`. */
  baseBillingPrice?: number | null;
  displayPriceIncludesAddons?: boolean;
  baseDuration: number;
  /** Enables patient-facing prep/buffer duration calculations in self-scheduling. */
  baseService?: ServiceTiny & {
    addonsUseLongestDuration?: boolean;
  };
  selectedAddons: PublicServiceAddon[];
  /** Without `baseService`, whether the add-ons run side by side. */
  addonsUseLongestDuration?: boolean;
  estimated?: boolean;
  priceStartsAt?: boolean;
}
export const SelectionsSummary = t0 => {
  const $ = _c(50);
  const {
    basePrice,
    basePriceMax,
    baseBillingPrice,
    displayPriceIncludesAddons,
    baseDuration,
    baseService,
    selectedAddons,
    addonsUseLongestDuration,
    estimated,
    priceStartsAt
  } = t0;
  let t1;
  if ($[0] !== addonsUseLongestDuration || $[1] !== selectedAddons) {
    t1 = count => computeAddonMinutes({
      addonsUseLongestDuration
    }, selectedAddons.slice(0, count).map(_temp));
    $[0] = addonsUseLongestDuration;
    $[1] = selectedAddons;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const addonMinutes = t1;
  const getAddonPricing = _temp2;
  let t2;
  if ($[3] !== addonMinutes || $[4] !== baseBillingPrice || $[5] !== baseDuration || $[6] !== basePrice || $[7] !== basePriceMax || $[8] !== baseService || $[9] !== displayPriceIncludesAddons || $[10] !== estimated || $[11] !== priceStartsAt || $[12] !== selectedAddons) {
    const addonPricings = selectedAddons.map(getAddonPricing);
    let t3;
    if ($[14] !== baseBillingPrice || $[15] !== displayPriceIncludesAddons || $[16] !== selectedAddons.length) {
      t3 = baseBillingPrice != null && quotesWithAddons({
        displayPriceIncludesAddons
      }, selectedAddons.length);
      $[14] = baseBillingPrice;
      $[15] = displayPriceIncludesAddons;
      $[16] = selectedAddons.length;
      $[17] = t3;
    } else {
      t3 = $[17];
    }
    const baseAtBillingPrice = t3;
    const baseMin = baseAtBillingPrice ? baseBillingPrice : basePrice;
    const baseMax = baseAtBillingPrice ? baseBillingPrice : Math.max(basePriceMax ?? 0, basePrice);
    const totalPriceMin = addonPricings.reduce(_temp3, baseMin);
    const totalPriceMax = addonPricings.reduce(_temp4, baseMax);
    let t4;
    if ($[18] !== baseDuration || $[19] !== baseService) {
      t4 = baseService ? patientFacingDurationLabel(baseService) : `${baseDuration}`;
      $[18] = baseDuration;
      $[19] = baseService;
      $[20] = t4;
    } else {
      t4 = $[20];
    }
    const displayedBaseDuration = t4;
    let t5;
    if ($[21] !== baseService || $[22] !== selectedAddons) {
      t5 = bound => computePatientFacingDurationWithAddons(atDurationBound(baseService, bound), selectedAddons.map(addon_1 => atDurationBound(addon_1.addonService, bound)));
      $[21] = baseService;
      $[22] = selectedAddons;
      $[23] = t5;
    } else {
      t5 = $[23];
    }
    const totalAt = t5;
    let t6;
    if ($[24] !== addonMinutes || $[25] !== baseDuration || $[26] !== baseService || $[27] !== selectedAddons.length || $[28] !== totalAt) {
      t6 = baseService ? formatDurationRange(totalAt("min"), totalAt("max")) : `${baseDuration + addonMinutes(selectedAddons.length)}`;
      $[24] = addonMinutes;
      $[25] = baseDuration;
      $[26] = baseService;
      $[27] = selectedAddons.length;
      $[28] = totalAt;
      $[29] = t6;
    } else {
      t6 = $[29];
    }
    const totalDuration = t6;
    const hasRange = totalPriceMax > totalPriceMin;
    const hasEstimated = !baseAtBillingPrice && estimated || addonPricings.some(_temp5);
    const hasStartsAt = !baseAtBillingPrice && priceStartsAt || addonPricings.some(_temp6);
    const formatTotalPrice = () => {
      if (totalPriceMin <= 0) {
        return null;
      }
      if (hasRange) {
        return `${formatCurrency(totalPriceMin)} - ${formatCurrency(totalPriceMax)}`;
      }
      if (hasStartsAt) {
        return `${formatCurrency(totalPriceMin)}+`;
      }
      if (hasEstimated) {
        return `from ${formatCurrency(totalPriceMin)}`;
      }
      return formatCurrency(totalPriceMin);
    };
    let t7;
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = addon_2 => {
        const pricing_3 = getAddonPricing(addon_2);
        const hasPromotedFreePrice = pricing_3.displayPrice === 0 && (!!pricing_3.promotionName || pricing_3.originalDisplayPrice != null);
        if (pricing_3.displayPrice <= 0 && !hasPromotedFreePrice) {
          return null;
        }
        const formatted = hasPromotedFreePrice ? formatCurrency(0) : formatDisplayPrice(pricing_3, formatCurrency);
        return formatted ? `+${formatted}` : null;
      };
      $[30] = t7;
    } else {
      t7 = $[30];
    }
    const formatAddonPrice = t7;
    let t8;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <span>Base</span>;
      $[31] = t8;
    } else {
      t8 = $[31];
    }
    let t9;
    if ($[32] !== baseAtBillingPrice || $[33] !== baseMin || $[34] !== basePrice || $[35] !== basePriceMax || $[36] !== estimated || $[37] !== priceStartsAt) {
      t9 = baseAtBillingPrice ? baseMin > 0 && <span>{formatCurrency(baseMin)}</span> : basePrice > 0 && <span>{formatDisplayPrice({
          displayPrice: basePrice,
          displayPriceMax: basePriceMax,
          priceStartsAt,
          estimated
        }, formatCurrency)}</span>;
      $[32] = baseAtBillingPrice;
      $[33] = baseMin;
      $[34] = basePrice;
      $[35] = basePriceMax;
      $[36] = estimated;
      $[37] = priceStartsAt;
      $[38] = t9;
    } else {
      t9 = $[38];
    }
    let t10;
    if ($[39] !== displayedBaseDuration) {
      t10 = <span className="min-w-14 whitespace-nowrap text-right">{displayedBaseDuration}min</span>;
      $[39] = displayedBaseDuration;
      $[40] = t10;
    } else {
      t10 = $[40];
    }
    let t11;
    if ($[41] !== t10 || $[42] !== t9) {
      t11 = <div className="flex items-center justify-between tabular-nums text-foreground-secondary">{t8}<div className="flex gap-3">{t9}{t10}</div></div>;
      $[41] = t10;
      $[42] = t9;
      $[43] = t11;
    } else {
      t11 = $[43];
    }
    let t12;
    if ($[44] !== addonMinutes || $[45] !== baseService || $[46] !== selectedAddons) {
      t12 = selectedAddons.map((addon_3, index) => {
        const addonPriceLabel = formatAddonPrice(addon_3);
        const addonDuration = baseService ? computePatientFacingDurationWithAddons(baseService, selectedAddons.slice(0, index + 1).map(_temp7)) - computePatientFacingDurationWithAddons(baseService, selectedAddons.slice(0, index).map(_temp8)) : addonMinutes(index + 1) - addonMinutes(index);
        return <div key={`${addon_3.id}:${index}`} className="flex items-center justify-between tabular-nums text-foreground-secondary"><span className="truncate pr-2 text-primary font-medium">{addon_3.addonService.name}</span><div className="flex gap-3 flex-shrink-0">{addonPriceLabel && <span>{addonPriceLabel}</span>}<span className="min-w-14 whitespace-nowrap text-right">+{addonDuration}min</span></div></div>;
      });
      $[44] = addonMinutes;
      $[45] = baseService;
      $[46] = selectedAddons;
      $[47] = t12;
    } else {
      t12 = $[47];
    }
    let t13;
    if ($[48] !== t12) {
      t13 = <div className="overflow-y-auto space-y-1.5 min-h-0 flex-1">{t12}</div>;
      $[48] = t12;
      $[49] = t13;
    } else {
      t13 = $[49];
    }
    t2 = <div className="space-y-1.5 text-body h-full flex flex-col justify-end" aria-live="polite">{t11}{t13}{selectedAddons.length > 0 && <><div className="border-t border-border" /><div className={cx("flex items-center justify-between tabular-nums font-medium text-foreground")}><span>Total</span><div className="flex gap-3">{formatTotalPrice() && <span>{formatTotalPrice()}</span>}<span className="min-w-14 whitespace-nowrap text-right">{totalDuration}min</span></div></div></>}</div>;
    $[3] = addonMinutes;
    $[4] = baseBillingPrice;
    $[5] = baseDuration;
    $[6] = basePrice;
    $[7] = basePriceMax;
    $[8] = baseService;
    $[9] = displayPriceIncludesAddons;
    $[10] = estimated;
    $[11] = priceStartsAt;
    $[12] = selectedAddons;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  return t2;
};
function _temp(addon) {
  return addon.addonService.duration;
}
function _temp2(addon_0) {
  return getServicePricingForSelfSchedule(addon_0.addonService);
}
function _temp3(sum, pricing) {
  return sum + pricing.displayPrice;
}
function _temp4(sum_0, pricing_0) {
  return sum_0 + Math.max(pricing_0.displayPriceMax ?? 0, pricing_0.displayPrice);
}
function _temp5(pricing_1) {
  return pricing_1.estimated;
}
function _temp6(pricing_2) {
  return pricing_2.priceStartsAt;
}
function _temp7(item) {
  return item.addonService;
}
function _temp8(item_0) {
  return item_0.addonService;
}